import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { useRehabStates } from 'context/rehabStates';
import { useTranslation } from 'context/translations';
import { simpleDashDate, simpleDate } from 'lib/date';
import { snakeCase } from 'lib/util';
import LocationEpisode from 'models/LocationEpisode';
import { RehabStateApiName } from 'models/RehabState';
import { activityQueryKeys } from 'services/api/activity';
import { episodeQueryKeys } from 'services/api/episode';
import { createDateChanges } from 'services/api/locationEpisode/dateChanges';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { BodySmall } from 'styles/typography';

const validateDateField = (date: Date | null, previousDate: Date | null, previousFieldName: string) => {
  if (!date) return 'Required field';
  if (previousDate && date! < previousDate) {
    return `Date must be the same date or a later date than ${previousFieldName} (${simpleDate(previousDate)})`;
  }
  return '';
};

export type EditDatesModalType = {
  setShow: (b: boolean) => void;
  patientName: string;
  locationEpisode: LocationEpisode;
};

export default function EditDatesModal({ setShow, patientName, locationEpisode }: EditDatesModalType) {
  const queryClient = useQueryClient();
  const { translate } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: createDateChanges,
  });
  const rehabStates = useRehabStates();
  const [errors, setErrors] = useState<Record<RehabStateApiName, string>>({} as Record<RehabStateApiName, string>);
  const [newDates, setNewDates] = useState<Record<RehabStateApiName, Date | null>>(
    {} as Record<RehabStateApiName, Date | null>
  );

  const submitDisabled = useMemo(() => {
    return Object.values(errors).some((error) => error) || Object.values(newDates).every((date) => !date);
  }, [errors, newDates]);

  const getLocationEpisodeRehabState = useCallback(
    (apiName: string) => {
      return locationEpisode.rehabStates.find((x) => x.apiName === apiName);
    },
    [locationEpisode.rehabStates]
  );

  const handleDateChange = useCallback(
    (apiName: RehabStateApiName, date: Date | null) => {
      if (date?.valueOf() !== getLocationEpisodeRehabState(apiName)?.enteredAt?.valueOf()) {
        setNewDates((prev) => ({ ...prev, [apiName]: date }));
      } else {
        setNewDates((prev) => {
          const newDates = { ...prev };
          delete newDates[apiName];
          return newDates;
        });
      }
    },
    [getLocationEpisodeRehabState]
  );

  const handleCancel = () => {
    setShow(false);
    setNewDates({} as Record<RehabStateApiName, Date>);
    setErrors({} as Record<RehabStateApiName, string>);
  };

  const handleSubmit = () => {
    const handleSuccess = (res: LocationEpisode) => {
      queryClient.invalidateQueries({ queryKey: episodeQueryKeys.show({ id: res.episodeId }) });
      queryClient.invalidateQueries({ queryKey: locationEpisodeQueryKeys.show({ id: res.id }) });
      queryClient.invalidateQueries({ queryKey: activityQueryKeys.index({ locationEpisodeId: res.id }) });
      handleCancel();
    };

    const payload = {
      locationEpisodeId: locationEpisode.id,
      states: rehabStates
        .filter((state) => getLocationEpisodeRehabState(state.apiName)?.enteredAt)
        .map((state) => ({
          apiName: snakeCase(state.apiName),
          enteredAt: simpleDashDate(newDates[state.apiName] ?? getLocationEpisodeRehabState(state.apiName)?.enteredAt)!,
        })),
    };

    mutate(payload, {
      onSuccess: handleSuccess,
    });
  };

  useEffect(() => {
    setErrors(() => ({
      ...rehabStates
        .map((state, i) => {
          const currentState = getLocationEpisodeRehabState(state.apiName);
          if (!currentState?.enteredAt) return [state.apiName, ''];

          const currentDate = state.apiName in newDates ? newDates[state.apiName] : currentState.enteredAt;
          const prevStateState = rehabStates[i - 1]?.state ?? '';
          const prevStateApiName = rehabStates[i - 1]?.apiName ?? '';
          const prevDate =
            prevStateApiName in newDates
              ? newDates[prevStateApiName]
              : (getLocationEpisodeRehabState(prevStateApiName)?.enteredAt ?? null);

          return [state.apiName, validateDateField(currentDate, prevDate, prevStateState)];
        })
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as Record<RehabStateApiName, string>),
    }));
  }, [newDates, rehabStates, locationEpisode.rehabStates, getLocationEpisodeRehabState]);

  return (
    <StandardModal title='Edit Dates' onCancel={handleCancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <FormContainer>
        <InfoText>Each field must have the same date or a later date selected than the field listed above it.</InfoText>
        {rehabStates.map((state) => (
          <InputGroup key={`edit-dates-${state.apiName}`} title={translate(state.state)} error={errors[state.apiName]}>
            <DatePicker
              $hasError={!!errors[state.apiName]}
              maxDate={new Date()}
              selected={
                state.apiName in newDates
                  ? newDates[state.apiName]
                  : (getLocationEpisodeRehabState(state.apiName)?.enteredAt ?? null)
              }
              onChange={(date) => handleDateChange(state.apiName, date)}
              disabled={!getLocationEpisodeRehabState(state.apiName)?.enteredAt}
            />
          </InputGroup>
        ))}
      </FormContainer>
      <Actions>
        <Button variant='ghost' onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitDisabled} loading={isPending}>
          Submit
        </Button>
      </Actions>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoText = styled(BodySmall)`
  margin: 0 0 24px;
`;
